import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Subject, Observable, BehaviorSubject, Subscription } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { UiService } from './ui.service';
import { Pagination } from '@classes/pagination/pagination';
import { PaginationRequest } from '@classes/pagination/pagination-request';

import { RecurringOrder } from '@classes/recurring-order/recurring-order';

@Injectable({
  providedIn: 'root',
})
export class RecurringOrdersService {
  recurringOrdersListChanged = new Subject<RecurringOrder[]>();
  selectedRecurringOrderChanged = new Subject<RecurringOrder>();
  paginationSubject = new BehaviorSubject<Pagination>(null);
  dateNow: Date;

  private baseUrl: string = environment.apiUrl + 'recurring-orders';
  private orders: RecurringOrder[];
  private lastQueryObj: {
    name: null;
    template: null;
    status: null;
    user: null;
    client: null;
    fromStartDate: null;
    fromCreatedOn: null;
    fromModifiedOn: null;
    createdBy: null;
  };
  private getListSub: Subscription;
  private selectedOrder: RecurringOrder;

  constructor(
    private http: HttpClient,
    private uiSrv: UiService,
  ) {}

  getList(request: PaginationRequest): void {
    this.dateNow = new Date();
    this.uiSrv.loadingStateChanged.next(true);
    this.recurringOrdersListChanged.next([]);

    let httpParams = new HttpParams();

    if (this.getListSub) {
      this.getListSub.unsubscribe();
    }

    if (request.pageSize && request.page) {
      httpParams = httpParams
        .append('pageSize', request.pageSize.toString())
        // .append("page", request.query !== this.lastQuery ? "1" : request.page.toString());
        .append(
          'page',
          request.query !== this.lastQueryObj ? '1' : request.page.toString(),
        );
    }

    if (request.query) {
      const filters = request.query;
      const filtersQuery = [];

      if (filters.identifier) {
        filtersQuery.push(`identifier==${filters.identifier}`);
      }

      if (filters.type) {
        if (filters.type !== 'all') {
          filtersQuery.push(`type==${filters.type}`);
        }
      }

      if (filters.name) {
        filtersQuery.push(`name@=${filters.name}`);
      }

      if (filters.driver) {
        filtersQuery.push(`driver@=${filters.driver}`);
      }

      if (filters.client) {
        filtersQuery.push(`client@=${filters.client}`);
      }

      if (filters.site) {
        filtersQuery.push(`site@=${filters.site}`);
      }

      if (filters.createdBy) {
        filtersQuery.push(`createdBy@=${filters.createdBy}`);
      }

      if (filters.urgency) {
        filtersQuery.push(`urgency@=${filters.urgency}`);
      }

      if (filters.sender) {
        filtersQuery.push(`sender@=${filters.sender}`);
      }

      if (filters.receiver) {
        filtersQuery.push(`receiver@=${filters.receiver}`);
      }

      httpParams = httpParams.append('Filters', filtersQuery.join());
    }

    if (request.sort) {
      if (request.sort !== '-startDate') {
        httpParams = httpParams.append('sorts', request.sort);
      }
    }

    // this.lastQuery = request.query;
    this.lastQueryObj = request.query;

    this.getListSub = this.http
      .get(this.baseUrl, { params: httpParams, observe: 'response' })
      .subscribe((result: HttpResponse<RecurringOrder[]>) => {
        this.uiSrv.loadingStateChanged.next(false);
        const pagination: Pagination = JSON.parse(
          result.headers.get('X-Pagination'),
        );

        this.orders = result.body;
        this.orders.map((order) => {
          order.startTime = this.calculStartTime(order);
        });
        this.recurringOrdersListChanged.next([...this.orders]);
        this.paginationSubject.next({ ...pagination });
      });
  }

  get(id: string): Observable<RecurringOrder> {
    return this.http.get<RecurringOrder>(`${this.baseUrl}/${id}`).pipe(
      map((orderFromApi) => {
        orderFromApi = RecurringOrder.fromObject(orderFromApi);
        orderFromApi.startTime = this.calculStartTime(orderFromApi);
        this.selectedOrder = orderFromApi;
        this.selectedRecurringOrderChanged.next(orderFromApi);
        return orderFromApi;
      }),
    );
  }

  get selectedId(): string {
    return this.selectedOrder ? this.selectedOrder.id : null;
  }

  put(order: RecurringOrder): Observable<RecurringOrder> {
    return this.http.put<RecurringOrder>(this.baseUrl, order).pipe(
      tap((orderFromApi) => {
        orderFromApi = RecurringOrder.fromObject(orderFromApi);
        orderFromApi.startTime = this.calculStartTime(orderFromApi);
        this.selectedRecurringOrderChanged.next(orderFromApi);
      }),
    );
  }

  post(mission: RecurringOrder): Observable<RecurringOrder> {
    return this.http.post<RecurringOrder>(this.baseUrl, mission);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      tap(() => {
        this.orders.map((order) => {
          if (order.id === id) {
            order.isDeleted = true;
          }
        });
        this.orders = this.orders.filter((m) => m.id !== id);
        this.recurringOrdersListChanged.next([...this.orders]);
      }),
    );
  }

  calculStartTime(order): string {
    let cron = order.cronExpression;

    if(!cron){
      return null ;
    }

    let minutesStartTime =
      parseInt(cron[0] + '' + cron[1]) + order.minutesToAdd;

    let hoursStartTime = parseInt(cron[3] + '' + cron[4]);

    let total = hoursStartTime * 60 + minutesStartTime;

    let m = total % 60;

    let h = (total - m) / 60;

    return h.toString() + ':' + (m < 10 ? '0' : '') + m.toString();
  }

  patchIsActive({ id }: RecurringOrder, status: boolean) {
    return this.http.patch(`${this.baseUrl}/${id}/is-active`, status).pipe(
      tap((result: RecurringOrder) => {
        this.orders = this.orders.map((order) => {
          return order.id === id
            ? ({ ...order, isActive: result.isActive } as RecurringOrder)
            : order;
        });
        this.recurringOrdersListChanged.next([...this.orders]);
      }),
    );
  }
}
